import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[1.15rem] border border-transparent text-sm font-semibold tracking-[-0.01em] ring-offset-background transition-[transform,box-shadow,background-color,border-color,color] duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:transform-none [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-gradient-primary text-primary-foreground shadow-button hover:-translate-y-0.5 hover:shadow-button-hover",
        destructive:
          "bg-destructive text-destructive-foreground shadow-button hover:-translate-y-0.5 hover:shadow-button-hover hover:bg-destructive/90",
        outline:
          "border-slate-200/80 bg-white/82 text-slate-700 shadow-soft backdrop-blur-xl hover:-translate-y-0.5 hover:border-primary/20 hover:bg-white hover:text-slate-950",
        secondary:
          "border-slate-200/70 bg-slate-100/80 text-slate-900 shadow-soft hover:-translate-y-0.5 hover:bg-white",
        ghost: "text-slate-600 hover:bg-white/75 hover:text-slate-950 hover:shadow-soft backdrop-blur-md",
        link: "text-primary underline-offset-4 hover:underline",
        success: "bg-[linear-gradient(135deg,#22c55e_0%,#16a34a_100%)] text-white shadow-button hover:-translate-y-0.5 hover:shadow-button-hover",
        warning: "bg-[linear-gradient(135deg,#fb923c_0%,#ea580c_100%)] text-white shadow-button hover:-translate-y-0.5 hover:shadow-button-hover",
        hero: "bg-gradient-primary text-white shadow-glow hover:-translate-y-0.5 hover:shadow-strong",
        chat: "bg-gradient-primary text-primary-foreground rounded-full shadow-button hover:-translate-y-0.5 hover:shadow-button-hover",
      },
      size: {
        default: "h-11 px-6 py-3",
        sm: "h-9 px-4 py-2 text-sm",
        lg: "h-12 px-8 py-4 text-base",
        xl: "h-14 px-10 py-5 text-lg",
        icon: "h-11 w-11",
        "icon-sm": "h-9 w-9",
        "icon-lg": "h-12 w-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
